import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeftRounded, ChevronRightRounded, ContentCopyRounded } from '@mui/icons-material';
import { Button, Chip } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import { copyValueToClipboard, priceToString } from 'shared/utils';
import { BUTTON_COLORS, VARIANTS } from 'shared/constants';
import { DataFormatter } from 'shared/components/data-display/DataFormatter';
import { getBuildingPath, getUnitPath } from 'shared/routes/building';
import { getSubscriptionPath, getTenantPath } from 'shared/routes/tenant';
import { useNotification } from 'shared/hooks';
import { TooltipIcon } from 'shared/components/abstract/interactables';

/**
 * @param {Object} props
 * @param {boolean} props.active
 * @param {Function} props.changeActiveVoucher
 * @param {string} props.container - One of: 'grid', 'row'
 * @param {Function} props.setActiveVoucher
 * @param {Object<string, *>} props.voucher
 */
export default function VoucherButton(props) {
  const {
    active,
    changeActiveVoucher,
    container,
    setActiveVoucher,
    voucher,
  } = props;

  const notification = useNotification();
  const copyToClipboard = async () => {
    await copyValueToClipboard(voucher.voucher);
    notification.pushInfo(`Copied "${voucher.voucher}" to clipboard`);
  };

  const isRedeemed = !!voucher.subscription;

  let voucherStatus;
  if (!isRedeemed) {
    voucherStatus = { bg: 'success', text: 'Available', className: 'voucher-grid-available' };
  } else if (isRedeemed && voucher.endTime) {
    voucherStatus = { bg: 'secondary', text: 'Ended', className: 'voucher-grid-ended' };
  } else {
    voucherStatus = { bg: 'info', text: 'Active', className: 'voucher-grid-active' };
  }
  if (container === 'row') voucherStatus.className = 'table-button-row';

  const voucherDescriptions = [
    {
      label: 'Tenant',
      formatter: (v) => <Link to={getTenantPath(v.tenantId)}>{v.tenantName}</Link>,
      skipWhen: (v) => !v.tenantId,
    },
    {
      label: 'Subscription',
      formatter: (v) => <Link to={getSubscriptionPath(v.tenantId)}>{v.subscription}</Link>,
      skipWhen: (v) => !v.tenantId || !v.subscription,
    },
    {
      label: 'Unit',
      formatter: (v) => <Link to={getUnitPath(v.propertyId, v.unitId)}>{v.unitName}</Link>,
      skipWhen: (v) => !v.propertyId || !v.unitId || !v.unitName,
    },
    {
      label: 'Building',
      formatter: (v) => <Link to={getBuildingPath(v.propertyId)}>{v.propertyName}</Link>,
      skipWhen: (v) => !v.propertyId || !v.propertyName,
    },
    {
      label: 'Bill',
      formatter: (v) => priceToString(v.billInCents),
      skipWhen: (v) => !v.billInCents,
    },
    {
      label: 'Start Time',
      key: 'startTime',
      skipWhen: (v) => !v.startTime,
    },
    {
      label: 'End Time',
      key: 'endTime',
      skipWhen: (v) => !v.endTime,
    },
    {
      label: 'Expected End',
      key: 'expectedEndTime',
      skipWhen: (v) => !v.expectedEndTime,
    },
  ];

  return (
    <>
      <Dialog
        open={active}
        onClose={() => setActiveVoucher('')}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <TooltipIcon
              icon={ChevronLeftRounded}
              onClick={() => changeActiveVoucher(-1)}
              message="Previous voucher"
            />
            <span>
              {voucher.voucher}
                &nbsp;&nbsp;
              <ContentCopyRounded className="clickable-icon" onClick={copyToClipboard} />
            </span>
            <TooltipIcon
              icon={ChevronRightRounded}
              onClick={() => changeActiveVoucher(1)}
              message="Next voucher"
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          <Chip color={voucherStatus.bg} label={`Status: ${voucherStatus.text}`} sx={{ marginBottom: '5px' }} />
          <DataFormatter
            data={voucher}
            descriptions={voucherDescriptions}
          />
        </DialogContent>
        <DialogActions>
          <Button
            color={VARIANTS.primary}
            variant={VARIANTS.contained}
            onClick={() => setActiveVoucher('')}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <div className={voucherStatus.className}>
        <Button
          className={`light-button table-button ${active ? 'table-button-active' : ''}`}
          onClick={() => setActiveVoucher(voucher.voucher)}
          variant={VARIANTS.outlined}
          color={BUTTON_COLORS.primary}
        >
          {voucher.voucher}
        </Button>
      </div>
    </>
  );
}
